import { APIProvider } from "@vis.gl/react-google-maps";
import { FC } from "react";

import InlineError from "src/components/Errors/InlineError.tsx";
import { DynamicViewData } from "src/dynamic-view-renderer/DynamicViewRenderer.typings.ts";
import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.tsx";
import {
  DashboardRowConfiguration,
  DashboardWidgetConfiguration,
} from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.types.ts";
import AlertWorkflowWidget from "src/dynamic-view-renderer/Widgets/AlertWorkflowWidget/AlertWorkflowWidget.tsx";
import BasicWidget from "src/dynamic-view-renderer/Widgets/BasicWidget/BasicWidget.tsx";
import ChronologyWidget from "src/dynamic-view-renderer/Widgets/ChronologyWidget.tsx";
import { ExecutionsWidget } from "src/dynamic-view-renderer/Widgets/ExecutionsWidget/ExecutionsWidget.tsx";
import GoogleMapsWidget, {
  GoogleMapsWidgetConfiguration,
} from "src/dynamic-view-renderer/Widgets/GoogleMapsWidget/GoogleMapsWidget.tsx";
import GoogleStreetViewWidget, {
  GoogleStreetViewWidgetConfiguration,
} from "src/dynamic-view-renderer/Widgets/GoogleMapsWidget/GoogleStreetViewWidget.tsx";
import { RelatedWidgetConfiguration } from "src/dynamic-view-renderer/Widgets/RelatedCases/relatedCases.typings.ts";
import RulesWidget from "src/dynamic-view-renderer/Widgets/RulesWidget/RulesWidget.tsx";
import WidgetContainer from "src/dynamic-view-renderer/Widgets/WidgetContainer.tsx";
import { useClientSchemaQuery } from "src/graphql/anti-fraud/queries/cases/GetCaseSchemasQuery.ts";
import { useGetGoogleMapsApiKeyQuery } from "src/graphql/anti-fraud/queries/googleMaps/GetGoogleMapsApiKey.ts";
import { useT } from "src/internationalization";

import CaseAlertsWidget from "../CaseAlertsWidget/CaseAlertsWidget.tsx";

import { ExecutionDetailWidget } from "./ExecutionsWidget/ExecutionDetailWidget";
import RelatedCasesWidget from "./RelatedCases/RelatedCasesWidget.tsx";

export type WidgetRendererProps = {
  data: DynamicViewData;
  widgetConfig: DashboardWidgetConfiguration;
  rowConfiguration: DashboardRowConfiguration;
};

const WidgetRenderer: FC<WidgetRendererProps> = ({ rowConfiguration, widgetConfig, data }) => {
  const uiSchema = useDynamicViewSchema();
  const { data: caseSchema, error: caseSchemaError } = useClientSchemaQuery();

  const widgetDefinitions = uiSchema.widgetDefinitions;
  const t = useT();

  const widgetDefinition = widgetDefinitions.find(
    (definition) => definition.code === widgetConfig.widgetCode
  );

  const { data: apiKey } = useGetGoogleMapsApiKeyQuery();

  if (caseSchemaError) {
    return (
      <WidgetContainer widgetConfig={widgetConfig} rowConfiguration={rowConfiguration}>
        <div className="text-supportError mb-4">{t("general.clientSchemaError")}</div>
      </WidgetContainer>
    );
  }

  if (!widgetDefinition) {
    console.warn("There is no widget definition with code: ", widgetConfig.widgetCode);

    return (
      <WidgetContainer widgetConfig={widgetConfig} rowConfiguration={rowConfiguration}>
        There is no widget definition with code: {widgetConfig.widgetCode}
      </WidgetContainer>
    );
  }

  const widgetContainerAttributes = {
    widgetConfig,
    rowConfiguration,
    widgetDefinition,
  };

  switch (widgetDefinition?.type) {
    case "rules":
      if (data.widgetsData.origin === "manual") {
        return null;
      }
      return (
        <RulesWidget
          {...widgetContainerAttributes}
          configuration={
            widgetDefinition.configuration?.customConfiguration as RelatedWidgetConfiguration
          }
          data={data.widgetsData.rules}
        />
      );
    case "chronology":
      return <ChronologyWidget {...widgetContainerAttributes} data={data.widgetsData.chronology} />;

    case "table": {
      //TODO: validate widgetDefinition.configuration?.customConfiguration
      return data.widgetsData.relatedCases ? (
        <WidgetContainer {...widgetContainerAttributes}>
          <RelatedCasesWidget
            data={data.widgetsData.relatedCases}
            configuration={
              widgetDefinition.configuration?.customConfiguration as RelatedWidgetConfiguration
            }
          />
        </WidgetContainer>
      ) : (
        <InlineError message={t("general.table.empty")} />
      );
    }

    case "alertWorkflow":
      return (
        <AlertWorkflowWidget {...widgetContainerAttributes} data={data.widgetsData.alertWorkflow} />
      );

    case "executions":
      return <ExecutionsWidget {...widgetContainerAttributes} data={data.widgetsData.executions} />;

    case "executionDetails":
      return (
        <ExecutionDetailWidget
          {...widgetContainerAttributes}
          data={data.widgetsData.executionDetails}
        />
      );

    case "googleMaps":
      return (
        <GoogleMapsWidget
          {...widgetContainerAttributes}
          configuration={
            widgetDefinition.configuration?.customConfiguration as
              | GoogleMapsWidgetConfiguration
              | undefined
          }
          data={data.widgetsData.googleMaps}
          apiKey={apiKey}
        />
      );

    case "googleStreetView":
      if (!apiKey) {
        return (
          <WidgetContainer {...widgetContainerAttributes}>
            <div className="text-supportError">Google Maps API key is required</div>
          </WidgetContainer>
        );
      }
      return (
        <APIProvider apiKey={apiKey}>
          <GoogleStreetViewWidget
            {...widgetContainerAttributes}
            data={data.widgetsData.googleStreetView}
            configuration={
              widgetDefinition.configuration?.customConfiguration as
                | GoogleStreetViewWidgetConfiguration
                | undefined
            }
          />
        </APIProvider>
      );

    case "caseAlerts":
      return <CaseAlertsWidget {...widgetContainerAttributes} data={data.widgetsData.caseAlerts} />;
    default:
      return (
        <BasicWidget
          {...widgetContainerAttributes}
          data={data.widgetsData.basic}
          caseSchema={caseSchema}
        />
      );
  }
};

export default WidgetRenderer;
